class MogaAccountPopup extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.panel = null;
    this.overlay = null;
    this.closeButtons = [];
    this.lastFocused = null;
    this.isOpen = false;

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onPanelMouseMove = this.onPanelMouseMove.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.toggle = this.toggle.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-moga-account-trigger]');
    this.panel = this.querySelector('[data-moga-account-panel]');
    this.overlay = this.querySelector('[data-moga-account-overlay]');
    this.closeButtons = Array.from(this.querySelectorAll('[data-moga-account-close]'));

    if (!this.trigger || !this.panel || !this.overlay) return;

    this.trigger.addEventListener('click', this.toggle);
    this.overlay.addEventListener('click', this.onOverlayClick);

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.close);
    });

    this.panel.addEventListener('mousemove', this.onPanelMouseMove);
  }

  disconnectedCallback() {
    if (this.trigger) this.trigger.removeEventListener('click', this.toggle);
    if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.close));
    if (this.panel) this.panel.removeEventListener('mousemove', this.onPanelMouseMove);

    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.lastFocused = document.activeElement;

    this.overlay.hidden = false;
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('moga-account-open');

    requestAnimationFrame(() => {
      const firstFocusable = this.getFocusableElements()[0] || this.panel;
      firstFocusable.focus({ preventScroll: true });
    });

    document.addEventListener('keydown', this.onDocumentKeydown);
    setTimeout(() => {
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    }, 0);
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.overlay.hidden = true;
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('moga-account-open');

    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus({ preventScroll: true });
    }
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    this.isOpen ? this.close() : this.open();
  }

  onOverlayClick(event) {
    event.preventDefault();
    this.close();
  }

  onDocumentPointerDown(event) {
    if (!this.isOpen) return;

    const target = event.target;

    if (this.panel.contains(target) || this.trigger.contains(target)) return;

    this.close();
  }

  onPanelMouseMove(event) {
    const rect = this.panel.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    this.panel.style.setProperty('--moga-account-x', `${x.toFixed(2)}%`);
    this.panel.style.setProperty('--moga-account-y', `${y.toFixed(2)}%`);
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.getFocusableElements();

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getFocusableElements() {
    if (!this.panel) return [];

    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      return !element.hasAttribute('disabled') && element.offsetParent !== null;
    });
  }
}

if (!customElements.get('moga-account-popup')) {
  customElements.define('moga-account-popup', MogaAccountPopup);
}